import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSmartEngagement } from '@/hooks/useSmartEngagement';

// Variants disponibles pour les messages contextuels
// Variants disponibles pour les messages contextuels
type BuyMeCoffeeVariant = 'planner' | 'gallery' | 'resources' | 'videos';

interface BuyMeCoffeeButtonProps {
    username?: string;
    floating?: boolean;
    variant?: BuyMeCoffeeVariant;
}

/**
 * Buy Me a Coffee button component
 * Can be used as a floating button or inline
 * Mobile-optimized with close button
 * Supports contextual messages per page via variant prop
 */
export const BuyMeCoffeeButton: React.FC<BuyMeCoffeeButtonProps> = ({
    username = 'stellarskyimager',
    floating = false,
    variant = 'planner'
}) => {
    const { t } = useTranslation();
    const bmcUrl = `https://www.buymeacoffee.com/${username}`;

    // Smart Engagement Logic
    // - 45s de temps
    // - 50% de scroll
    // - 3 jours de silence si fermé
    const { shouldShow, dismiss } = useSmartEngagement({
        timeThreshold: 45000,
        scrollThreshold: 50,
        coolingPeriodDays: 3
    });

    // Planner sur mobile web : remonte le bouton au-dessus de la barre fixe « Calculer »
    const plannerOffset = variant === 'planner' ? 'max-lg:!bottom-28' : '';

    // État local pour le toggle (réduire/agrandir) - indépendant de l'apparition
    const [isExpanded, setIsExpanded] = useState(true);

    // Clés de traduction selon le variant
    const getTranslationKey = (key: string) => {
        // Le planner utilise les clés par défaut pour rétrocompatibilité
        if (variant === 'planner') {
            return `common.buyMeCoffee.${key}`;
        }
        return `common.buyMeCoffee.variants.${variant}.${key}`;
    };

    const handleClose = () => {
        dismiss();
    };

    const handleMinimize = () => {
        setIsExpanded(false);
    };

    const handleExpand = () => {
        setIsExpanded(true);
    };

    if (floating) {
        // On n'affiche rien tant que le smart trigger n'est pas activé
        if (!shouldShow) return null;

        // Minimized version (just the coffee icon button)
        if (!isExpanded) {
            return (
                <button
                    onClick={handleExpand}
                    className={`fixed ${plannerOffset} bottom-4 right-4 z-40 bg-[#FFDD00] hover:bg-[#FFCC00] text-black p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110 animate-fadeIn`}
                    aria-label={t('common.buyMeCoffee.ariaShow')}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        className="w-5 h-5 fill-current"
                    >
                        <path d="M20.216 6.415l-.132-.666c-.119-.598-.388-1.163-1.001-1.379-.197-.069-.42-.098-.57-.241-.152-.143-.196-.366-.231-.572-.065-.378-.125-.756-.192-1.133-.057-.325-.102-.69-.25-.987-.195-.4-.597-.634-.996-.788a5.723 5.723 0 00-.626-.194c-1-.263-2.05-.36-3.077-.416a25.834 25.834 0 00-3.7.062c-.915.083-1.88.184-2.75.5-.318.116-.646.256-.888.501-.297.302-.393.77-.177 1.146.154.267.415.456.692.58.36.162.737.284 1.123.366 1.075.238 2.189.331 3.287.37 1.218.05 2.437.01 3.65-.118.299-.033.598-.073.896-.119.352-.054.578-.513.474-.834-.124-.383-.457-.531-.834-.473-.466.074-.96.108-1.382.146-1.177.08-2.358.082-3.536.006a22.228 22.228 0 01-1.157-.107c-.086-.01-.18-.025-.258-.036-.243-.036-.484-.08-.724-.13-.111-.027-.111-.185 0-.212h.005c.277-.06.557-.108.838-.147h.002c.131-.009.263-.032.394-.048a25.076 25.076 0 013.426-.12c.674.019 1.347.067 2.017.144l.228.031c.267.04.533.088.798.145.392.085.895.113 1.07.542.055.137.08.288.111.431l.319 1.484a.237.237 0 01-.199.284h-.003c-.037.006-.075.01-.112.015a36.704 36.704 0 01-4.743.295 37.059 37.059 0 01-4.699-.304c-.14-.017-.293-.042-.417-.06-.326-.048-.649-.108-.973-.161-.393-.065-.768-.032-1.123.161-.29.16-.527.404-.675.701-.154.316-.199.66-.267 1-.069.34-.176.707-.135 1.056.087.753.613 1.365 1.37 1.502a39.69 39.69 0 0011.343.376.483.483 0 01.535.53l-.071.697-1.018 9.907c-.041.41-.047.832-.125 1.237-.122.637-.553 1.028-1.182 1.171-.577.131-1.165.2-1.756.205-.656.004-1.31-.025-1.966-.022-.699.004-1.556-.06-2.095-.58-.475-.458-.54-1.174-.605-1.793l-.731-7.013-.322-3.094c-.037-.351-.286-.695-.678-.678-.336.015-.718.3-.678.679l.228 2.185.949 9.112c.147 1.344 1.174 2.068 2.446 2.272.742.12 1.503.144 2.257.156.966.016 1.942.053 2.892-.122 1.408-.258 2.465-1.198 2.616-2.657.34-3.332.906-6.63 1.276-9.96l.21-2.022a.311.311 0 01.279-.26c.992-.093 1.97-.279 2.877-.64 1.249-.497 2.032-1.556 1.967-2.951-.058-1.264-.68-2.296-1.774-2.863l-.145-.08c-.473-.239-1.003-.477-1.584-.585-.522-.097-1.057-.137-1.59-.137-.374 0-.748.018-1.12.053a.36.36 0 01-.355-.46c.027-.096.04-.196.063-.293z" />
                    </svg>
                </button>
            );
        }

        return (
            <div className={`fixed ${plannerOffset} bottom-4 right-4 sm:bottom-6 sm:right-6 z-40 group animate-slideInUp`}>
                {/* Main container with glass effect - responsive sizing */}
                <div className="bg-zinc-900/95 backdrop-blur-sm border border-zinc-700 rounded-2xl shadow-2xl shadow-black/50 p-3 sm:p-4 max-w-[220px] sm:max-w-[260px] relative">
                    {/* Control buttons (minimize & close) */}
                    <div className="absolute -top-2 -right-2 flex gap-1">
                        {/* Minimize button */}
                        <button
                            onClick={handleMinimize}
                            className="bg-zinc-700 hover:bg-zinc-600 text-zinc-300 hover:text-white w-6 h-6 rounded-full flex items-center justify-center transition-colors shadow-lg"
                            aria-label={t('common.buyMeCoffee.ariaMinimize')}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3">
                                <path fillRule="evenodd" d="M4 10a.75.75 0 01.75-.75h10.5a.75.75 0 010 1.5H4.75A.75.75 0 014 10z" clipRule="evenodd" />
                            </svg>
                        </button>
                        {/* Close button */}
                        <button
                            onClick={handleClose}
                            className="bg-zinc-700 hover:bg-red-500 text-zinc-300 hover:text-white w-6 h-6 rounded-full flex items-center justify-center transition-colors shadow-lg"
                            aria-label={t('common.buyMeCoffee.ariaClose')}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3">
                                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                            </svg>
                        </button>
                    </div>

                    {/* Header with heart */}
                    <div className="flex items-center gap-2 mb-1.5 sm:mb-2">
                        <span className="text-red-500 animate-pulse text-sm sm:text-base">❤️</span>
                        <span className="text-white text-xs sm:text-sm font-bold">{t(getTranslationKey('freeTool'))}</span>
                    </div>

                    {/* Message - hidden on very small screens */}
                    <p className="text-zinc-400 text-xs mb-2 sm:mb-3 leading-relaxed hidden sm:block">
                        {t(getTranslationKey('message'))}
                    </p>
                    <p className="text-zinc-400 text-[10px] mb-2 leading-relaxed sm:hidden">
                        {t(getTranslationKey('messageShort'))}
                    </p>

                    {/* BMC Button */}
                    <a
                        href={bmcUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 bg-[#FFDD00] hover:bg-[#FFCC00] text-black font-bold px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl transition-all duration-300 hover:scale-[1.02] shadow-lg w-full"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            className="w-4 h-4 sm:w-5 sm:h-5 fill-current"
                        >
                            <path d="M20.216 6.415l-.132-.666c-.119-.598-.388-1.163-1.001-1.379-.197-.069-.42-.098-.57-.241-.152-.143-.196-.366-.231-.572-.065-.378-.125-.756-.192-1.133-.057-.325-.102-.69-.25-.987-.195-.4-.597-.634-.996-.788a5.723 5.723 0 00-.626-.194c-1-.263-2.05-.36-3.077-.416a25.834 25.834 0 00-3.7.062c-.915.083-1.88.184-2.75.5-.318.116-.646.256-.888.501-.297.302-.393.77-.177 1.146.154.267.415.456.692.58.36.162.737.284 1.123.366 1.075.238 2.189.331 3.287.37 1.218.05 2.437.01 3.65-.118.299-.033.598-.073.896-.119.352-.054.578-.513.474-.834-.124-.383-.457-.531-.834-.473-.466.074-.96.108-1.382.146-1.177.08-2.358.082-3.536.006a22.228 22.228 0 01-1.157-.107c-.086-.01-.18-.025-.258-.036-.243-.036-.484-.08-.724-.13-.111-.027-.111-.185 0-.212h.005c.277-.06.557-.108.838-.147h.002c.131-.009.263-.032.394-.048a25.076 25.076 0 013.426-.12c.674.019 1.347.067 2.017.144l.228.031c.267.04.533.088.798.145.392.085.895.113 1.07.542.055.137.08.288.111.431l.319 1.484a.237.237 0 01-.199.284h-.003c-.037.006-.075.01-.112.015a36.704 36.704 0 01-4.743.295 37.059 37.059 0 01-4.699-.304c-.14-.017-.293-.042-.417-.06-.326-.048-.649-.108-.973-.161-.393-.065-.768-.032-1.123.161-.29.16-.527.404-.675.701-.154.316-.199.66-.267 1-.069.34-.176.707-.135 1.056.087.753.613 1.365 1.37 1.502a39.69 39.69 0 0011.343.376.483.483 0 01.535.53l-.071.697-1.018 9.907c-.041.41-.047.832-.125 1.237-.122.637-.553 1.028-1.182 1.171-.577.131-1.165.2-1.756.205-.656.004-1.31-.025-1.966-.022-.699.004-1.556-.06-2.095-.58-.475-.458-.54-1.174-.605-1.793l-.731-7.013-.322-3.094c-.037-.351-.286-.695-.678-.678-.336.015-.718.3-.678.679l.228 2.185.949 9.112c.147 1.344 1.174 2.068 2.446 2.272.742.12 1.503.144 2.257.156.966.016 1.942.053 2.892-.122 1.408-.258 2.465-1.198 2.616-2.657.34-3.332.906-6.63 1.276-9.96l.21-2.022a.311.311 0 01.279-.26c.992-.093 1.97-.279 2.877-.64 1.249-.497 2.032-1.556 1.967-2.951-.058-1.264-.68-2.296-1.774-2.863l-.145-.08c-.473-.239-1.003-.477-1.584-.585-.522-.097-1.057-.137-1.59-.137-.374 0-.748.018-1.12.053a.36.36 0 01-.355-.46c.027-.096.04-.196.063-.293z" />
                        </svg>
                        <span className="text-xs sm:text-sm">{t('common.buyMeCoffee.button')}</span>
                    </a>
                </div>
            </div>
        );
    }

    // Inline version (for footer or other placements)
    return (
        <a
            href={bmcUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#FFDD00] hover:bg-[#FFCC00] text-black font-bold px-4 py-2 rounded-lg transition-all duration-200 hover:scale-105 text-sm"
        >
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                className="w-4 h-4 fill-current"
            >
                <path d="M20.216 6.415l-.132-.666c-.119-.598-.388-1.163-1.001-1.379-.197-.069-.42-.098-.57-.241-.152-.143-.196-.366-.231-.572-.065-.378-.125-.756-.192-1.133-.057-.325-.102-.69-.25-.987-.195-.4-.597-.634-.996-.788a5.723 5.723 0 00-.626-.194c-1-.263-2.05-.36-3.077-.416a25.834 25.834 0 00-3.7.062c-.915.083-1.88.184-2.75.5-.318.116-.646.256-.888.501-.297.302-.393.77-.177 1.146.154.267.415.456.692.58.36.162.737.284 1.123.366 1.075.238 2.189.331 3.287.37 1.218.05 2.437.01 3.65-.118.299-.033.598-.073.896-.119.352-.054.578-.513.474-.834-.124-.383-.457-.531-.834-.473-.466.074-.96.108-1.382.146-1.177.08-2.358.082-3.536.006a22.228 22.228 0 01-1.157-.107c-.086-.01-.18-.025-.258-.036-.243-.036-.484-.08-.724-.13-.111-.027-.111-.185 0-.212h.005c.277-.06.557-.108.838-.147h.002c.131-.009.263-.032.394-.048a25.076 25.076 0 013.426-.12c.674.019 1.347.067 2.017.144l.228.031c.267.04.533.088.798.145.392.085.895.113 1.07.542.055.137.08.288.111.431l.319 1.484a.237.237 0 01-.199.284h-.003c-.037.006-.075.01-.112.015a36.704 36.704 0 01-4.743.295 37.059 37.059 0 01-4.699-.304c-.14-.017-.293-.042-.417-.06-.326-.048-.649-.108-.973-.161-.393-.065-.768-.032-1.123.161-.29.16-.527.404-.675.701-.154.316-.199.66-.267 1-.069.34-.176.707-.135 1.056.087.753.613 1.365 1.37 1.502a39.69 39.69 0 0011.343.376.483.483 0 01.535.53l-.071.697-10.18 9.907c-.041.41-.047.832-.125 1.237-.122.637-.553 1.028-1.182 1.171-.577.131-1.165.2-1.756.205-.656.004-1.31-.025-1.966-.022-.699.004-1.556-.06-2.095-.58-.475-.458-.54-1.174-.605-1.793l-.731-7.013-.322-3.094c-.037-.351-.286-.695-.678-.678-.336.015-.718.3-.678.679l.228 2.185.949 9.112c.147 1.344 1.174 2.068 2.446 2.272.742.12 1.503.144 2.257.156.966.016 1.942.053 2.892-.122 1.408-.258 2.465-1.198 2.616-2.657.34-3.332.906-6.63 1.276-9.96l.21-2.022a.311.311 0 01.279-.26c.992-.093 1.97-.279 2.877-.64 1.249-.497 2.032-1.556 1.967-2.951-.058-1.264-.68-2.296-1.774-2.863l-.145-.08c-.473-.239-1.003-.477-1.584-.585-.522-.097-1.057-.137-1.59-.137-.374 0-.748.018-1.12.053a.36.36 0 01-.355-.46c.027-.096.04-.196.063-.293z" />
            </svg>
            {t('common.buyMeCoffee.action')}
        </a>
    );
};

